import { Box } from '@material-ui/core';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useWidth } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useWidth';
import { PAYMENT_INTENT_CONTAINER_ID } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/CheckoutSessionDetail';
import { CreditCardPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/CreditCard/_shared/entities/PaymentMethodDetail/CreditCardPaymentMethodDetailEntity';
import { useMountCreditCardPaymentIntentMutation } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/CreditCard/hooks/useMountCreditCardPaymentIntentMutation';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { UserEntity } from '@/entities/UserEntity';
import { isIbcStandsAgentUser, isIbcStandsSupervisorUser } from '@/hooks/layoutConstructor/iberiaUserHelpers';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export const ContinueCreditCardPurchase = ({
  paymentMethodDetailEntity,
}: {
  paymentMethodDetailEntity: CreditCardPaymentMethodDetailEntity;
}) => {
  const { userEntity } = useUserContext();
  const mountCreditCardPaymentIntentMutation = useMountCreditCardPaymentIntentMutation({
    domSelector: `#${PAYMENT_INTENT_CONTAINER_ID}`,
    paymentMethodDetailEntity,
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const progress = paymentMethodDetailEntity.progress;
  const blockedInstructions = getBlockedInstructions(progress, userEntity);
  const canContinue = !progress?.isFinished && !blockedInstructions;

  useEffect(() => {
    // Open *once* the payment intent if continue param is present (set right after creating the application)
    // and the user can continue it, saving the click on the continue button. Waits for the refresh so the progress,
    // which decides whether the user can continue, is loaded; the param is dropped either way so it never fires later
    const continueParam = !!searchParams.get('continue');
    if (!continueParam || !paymentMethodDetailEntity.isRefreshed) {
      return;
    }

    searchParams.delete('continue');
    setSearchParams(searchParams);

    if (canContinue && !mountCreditCardPaymentIntentMutation.isLoading) {
      mountCreditCardPaymentIntentMutation.mutateAsync();
    }
  }, [paymentMethodDetailEntity.isRefreshed, canContinue, searchParams]);

  if (progress?.isFinished) {
    return null;
  }

  if (blockedInstructions) {
    return <ContinueCreditCardPurchaseLayout title="CREDIT_CARD_PURCHASE_TITLE" instructions={blockedInstructions} />;
  }

  return (
    <ContinueCreditCardPurchaseLayout
      title="CREDIT_CARD_PURCHASE_TITLE"
      instructions={['CREDIT_CARD_PURCHASE_FIRST_INSTRUCTIONS']}
      action={{
        label: 'CREDIT_CARD_PURCHASE_CONTINUE',
        onClick: mountCreditCardPaymentIntentMutation.mutateAsync,
        isDisabled: mountCreditCardPaymentIntentMutation.isLoading || mountCreditCardPaymentIntentMutation.isSuccess,
      }}
    />
  );
};

// Returns the instructions to show when the user cannot continue the application, or undefined when they can.
// Stands agents complete every step with the client on the iPad. Stands supervisors can only pick up an application
// once the summary is completed (scoring), when the data has gone through the identity checks; from there they can
// continue any step or share the link with the client.
const getBlockedInstructions = (
  progress: CreditCardPaymentMethodDetailEntity['progress'],
  userEntity: UserEntity | undefined,
): string[] | undefined => {
  if (!progress) {
    return undefined;
  }

  if (userEntity && isIbcStandsAgentUser(userEntity)) {
    return undefined;
  }

  if (userEntity && isIbcStandsSupervisorUser(userEntity)) {
    return progress.hasPassedScoring ? undefined : ['CREDIT_CARD_PURCHASE_STANDS_SUPERVISOR_PRE_SCORING_INSTRUCTIONS'];
  }

  return progress.shouldClientPerformTheSteps
    ? ['CREDIT_CARD_PURCHASE_SECOND_INSTRUCTIONS_1', 'CREDIT_CARD_PURCHASE_SECOND_INSTRUCTIONS_2']
    : undefined;
};

const ContinueCreditCardPurchaseLayout = ({
  title,
  instructions,
  action,
}: {
  title: string;
  instructions: string[];
  action?: {
    label: string;
    onClick: () => void;
    isLoading?: boolean;
    isDisabled?: boolean;
    errMessage?: string;
  };
}) => {
  const { palette } = useCustomTheme();
  const { ref, width } = useWidth<HTMLDivElement>();
  const isSmall = width && width < 500;

  return (
    <CustomPaper fill="paper">
      <div ref={ref}>
        <Box
          gridGap={24}
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexDirection={isSmall ? 'column' : 'row'}
        >
          <FlexColumn gridGap={4}>
            <Font variant="subtitle1" color="textPrimary" display="block">
              <Text strong tId={title} />
            </Font>
            {instructions.map((instruction, i) => (
              <Font variant="body2" color="textPrimary" key={i}>
                <Text tId={instruction} />
              </Font>
            ))}
          </FlexColumn>
          {action && (
            <Box flexShrink={0}>
              <StandardButton
                main
                size="medium"
                disabled={action.isDisabled}
                loading={action.isLoading}
                onClick={action.onClick}
              >
                <Text tId={action.label} />
              </StandardButton>
            </Box>
          )}
        </Box>
        {action && action.errMessage && (
          <FlexRow alignItems={'center'} justifyContent={'flex-start'} mt={1}>
            <Icon size={18} color={palette.error.main}>
              error
            </Icon>
            <Font variant="body2" color="error">
              <Text tId={action.errMessage} />
            </Font>
          </FlexRow>
        )}
      </div>
    </CustomPaper>
  );
};
